import { NgIf } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CountdownTimerService } from '../../../service/countdown-timer.service';
import { DeliveryMessagingService } from '../../../service/delivery-messaging.service';
import { EnvironmentService } from '../../../service/environment.service';

@Component({
  selector: 'app-motivator',
  imports: [NgIf],
  templateUrl: './motivator.component.html',
  styleUrl: './motivator.component.scss',
})
export class MotivatorComponent implements OnChanges {
  @Input() config: any;
  @Input() cart: any;

  _message: string = '';
  _success: string = '';
  _expressShippingMessage: string = '';
  _total: any = 0;
  _maxthreshold: number = 0;
  _progress: any = 0;

  constructor(
    private environmentService: EnvironmentService,
    private deliveryMessagingService: DeliveryMessagingService,
    private countdownTimerService: CountdownTimerService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this._total =
      parseFloat(this.cart?.cost?.subtotalAmount?.amount ?? '0') * 100;

    if (this.config?.motivator?.enabled !== true) return;
    this.config.motivator.stages.sort(
      (a: any, b: any) => a.threshold - b.threshold
    );
    this._success = '';
    this._message = '';
    this._expressShippingMessage = '';

    // Check if order exceeds $100 for express shipping message
    if (this._total >= 10000) {
      // $100 in cents
      this.generateExpressShippingMessage();
    }

    for (let entry of this.config.motivator.stages) {
      if (this._total < entry.threshold) {
        this._message = entry.message;
      }
      if (this._total > entry.threshold) {
        this._success = entry.success;
      }
      this._maxthreshold = entry.threshold;
      this.processMessage(entry);
    }
  }

  generateExpressShippingMessage() {
    // Get user location to populate state code and calculate delivery
    this.environmentService.getUserLocation().subscribe((location) => {
      if (
        location &&
        this.deliveryMessagingService.isUserInAustralia(location.country)
      ) {
        // Use the delivery messaging service to get proper delivery calculation
        this.deliveryMessagingService
          .getAustralianDeliveryMessageWithCountdown(location.region)
          .subscribe((deliveryMessage) => {
            if (deliveryMessage) {
              // Get countdown to AEST 12pm deadline
              this.countdownTimerService
                .getCountdownToDeadline()
                .subscribe((countdown) => {
                  const countdownText = !countdown.isExpired
                    ? ` ${countdown.hours}h ${countdown.minutes}m`
                    : '';
                  const stateCode = location.region || 'your state';

                  this._expressShippingMessage = `<strong>Free Express Shipping Applied! 🎉</strong><br>Order within${countdownText} and receive your order ${this.calculateDeliveryDate(
                    location.region
                  )} (based on delivery to ${stateCode})`;
                });
            }
          });
      } else {
        // Fallback for non-Australian users or when location is not available
        const stateCode = location?.region || 'your state';
        this._expressShippingMessage = `<strong>Free Express Shipping Applied! 🎉</strong><br>Order within ${this.getTimeUntilMidnight()} and receive your order ${this.calculateDeliveryDate(
          location?.region
        )} (based on delivery to ${stateCode})`;
      }
    });
  }

  calculateDeliveryDate(stateCode?: string): string {
    const now = new Date();
    let deliveryDate = new Date(now);

    if (stateCode) {
      const stateCodeUpper = stateCode.toUpperCase();

      // Calculate business days based on state
      let businessDays = 1; // Default for VIC, NSW, SA, TAS

      switch (stateCodeUpper) {
        case 'QLD':
        case 'WA':
          businessDays = 2;
          break;
        case 'NT':
          businessDays = 3;
          break;
        default:
          businessDays = 1;
          break;
      }

      // Check if we're past the 12pm AEST cutoff time
      // Use the same logic as the countdown timer service
      const userTimezoneOffset = now.getTimezoneOffset() * 60000; // Convert to milliseconds
      const aestOffset = 10 * 60 * 60000; // AEST is UTC+10 (in milliseconds)

      // Create deadline for today at 12pm AEST
      const deadlineAEST = new Date(now);
      deadlineAEST.setHours(12, 0, 0, 0);

      // Convert to UTC by adding user's timezone offset
      const deadlineUTC = deadlineAEST.getTime() + userTimezoneOffset;

      // Adjust for AEST timezone
      const deadlineInAEST = deadlineUTC + aestOffset;

      // Get current time in AEST
      const nowInAEST = now.getTime() + userTimezoneOffset + aestOffset;

      // If after cutoff, add an extra business day
      if (nowInAEST >= deadlineInAEST) {
        businessDays += 1;
      }

      // Add business days, skipping weekends
      let daysAdded = 0;
      let currentDate = new Date(now);

      while (daysAdded < businessDays) {
        currentDate.setDate(currentDate.getDate() + 1);
        const dayOfWeek = currentDate.getDay();
        // Skip weekends (0 = Sunday, 6 = Saturday)
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          daysAdded++;
        }
      }

      deliveryDate = currentDate;

      // Debug logging to verify calculation
      // console.log(`Order placed on: ${now.toDateString()}`);
      // console.log(`After cutoff: ${nowInAEST >= deadlineInAEST}`);
      // console.log(`Business days to add: ${businessDays}`);
      // console.log(`Delivery date: ${deliveryDate.toDateString()}`);
    } else {
      // Fallback: add 2 days
      deliveryDate.setDate(now.getDate() + 2);
    }

    const dayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];

    return dayNames[deliveryDate.getDay()];
  }

  getTimeUntilMidnight(): string {
    const now = new Date();
    const midnight = new Date();
    midnight.setHours(23, 59, 59, 999);

    const diff = midnight.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }

  processMessage(stage: any) {
    this._message = this._message.replace(
      '%amount%',
      '' +
        new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: this.cart.currency ?? 'USD',
        }).format((stage.threshold - this._total) / 100)
    );
  }

  processProgress(stage: any) {
    this._progress = this._total;
  }
}
